import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  /** Quantidade ao lado do nome (ex.: 3 filiais). Ausente = sem contador. */
  count?: number;
  /** Há campo com erro nesta aba (formulário em abas): ponto vermelho ao lado do nome. */
  error?: boolean;
}

/** Próxima aba com as setas, Home e End (dá a volta nas pontas). */
export function nextTabIndex(current: number, total: number, key: string): number {
  if (key === 'Home') return 0;
  if (key === 'End') return total - 1;
  if (key === 'ArrowRight') return (current + 1) % total;
  if (key === 'ArrowLeft') return (current - 1 + total) % total;
  return current;
}

/**
 * Abas do guia visual (fichas com várias partes). Só a aba ativa entra na ordem do Tab; as setas
 * trocam de aba. No celular a faixa rola na horizontal.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  idPrefix,
}: {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Nome da faixa de abas, para leitores de tela. */
  label: string;
  /** Liga cada aba ao seu painel (`<prefixo>-panel-<id>`). */
  idPrefix: string;
}) {
  const { t } = useTranslation();
  const errorLabel = t('common.tabHasErrors');
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKeyDown = (e: KeyboardEvent, index: number) => {
    const next = nextTabIndex(index, items.length, e.key);
    if (next === index) return;
    e.preventDefault();
    onChange(items[next]!.id);
    refs.current[next]?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label={label}
      className="ui-tabs flex gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:thin]"
    >
      {items.map((item, index) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${item.id}`}
            aria-selected={active}
            aria-controls={`${idPrefix}-panel-${item.id}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={`ui-tab -mb-px flex min-h-10 shrink-0 items-center gap-1.5 px-3 text-sm whitespace-nowrap ${
              active ? 'font-semibold text-ink' : 'text-muted hover:text-ink'
            }`}
          >
            {item.label}
            {item.error && (
              <span className="size-2 rounded-full bg-danger" role="img" aria-label={errorLabel} />
            )}
            {item.count !== undefined && (
              <span className="rounded-full bg-paper px-1.5 text-xs font-medium text-muted tabular-nums">
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Painel de uma aba, ligado a ela para leitores de tela. */
export function TabPanel({
  idPrefix,
  id,
  standalone = false,
  children,
}: {
  idPrefix: string;
  id: string;
  /** Sem faixa de abas (só uma parte visível): o conteúdo aparece sem o papel de painel. */
  standalone?: boolean;
  children: ReactNode;
}) {
  if (standalone) return <div className="flex flex-col gap-4">{children}</div>;
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${id}`}
      aria-labelledby={`${idPrefix}-tab-${id}`}
      className="flex flex-col gap-4"
    >
      {children}
    </div>
  );
}

/**
 * Aba aberta guardada no endereço (`?tab=…`), para dar para mandar o link de uma ficha em tela
 * completa já na aba certa. Troca só a parte "?…", sem criar entrada nova no histórico.
 */
export function useTabInUrl<T extends string>(
  tabs: readonly T[],
  fallback: T,
): [T, (tab: T) => void] {
  // A lista de abas pode chegar depois (dados ainda carregando): a aba do endereço vale assim que
  // existir; até lá, e se não existir, vale a primeira.
  const [tab, setTab] = useState<T>(
    () => (new URLSearchParams(window.location.search).get('tab') as T | null) ?? fallback,
  );
  const change = (next: T) => {
    setTab(next);
    const params = new URLSearchParams(window.location.search);
    if (next === fallback) params.delete('tab');
    else params.set('tab', next);
    const search = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}`,
    );
  };
  // Uma aba que deixou de existir (ex.: sem permissão) volta para a primeira.
  return [tabs.includes(tab) ? tab : fallback, change];
}
